/**
 * StreamingMarkdown - Component for smooth LLM output streaming with proper markdown handling
 *
 * Uses @llm-ui/react to:
 * - Smooth token output to native frame rate
 * - Hide broken markdown syntax during streaming
 * - Strip internal code/JSON blocks from chat display
 */

import { Component, type ReactNode, type ErrorInfo, useEffect, useState } from 'react';
import { useLLMOutput, type LLMOutputComponent } from '@llm-ui/react';
import { markdownLookBack } from '@llm-ui/markdown';
import ReactMarkdown from 'react-markdown';
import {
  chatRemarkPlugins,
  chatRehypePlugins,
  chatMarkdownComponents,
  stripCodeBlocks,
  preprocessContent,
} from '@/lib/markdown';

/**
 * Lightweight error boundary specifically for markdown rendering.
 * Falls back to plain text if markdown parsing fails.
 */
class MarkdownErrorBoundary extends Component<
  { children: ReactNode; fallbackContent: string },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallbackContent: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn(
      'Markdown rendering failed, falling back to plain text:',
      error.message,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      // Fallback to plain text with preserved whitespace
      return <pre className="whitespace-pre-wrap font-sans">{this.props.fallbackContent}</pre>;
    }
    return this.props.children;
  }
}

interface StreamingMarkdownProps {
  content: string;
  isStreaming: boolean;
}

/**
 * Markdown renderer component for llm-ui
 */
const MarkdownComponent: LLMOutputComponent = ({ blockMatch }) => {
  const markdown = blockMatch.output;
  const cleaned = stripCodeBlocks(markdown);

  return (
    <MarkdownErrorBoundary fallbackContent={cleaned}>
      <ReactMarkdown
        remarkPlugins={chatRemarkPlugins}
        rehypePlugins={chatRehypePlugins}
        components={chatMarkdownComponents}
      >
        {preprocessContent(cleaned)}
      </ReactMarkdown>
    </MarkdownErrorBoundary>
  );
};

/**
 * Force-finish a "streaming" bubble whose content has gone static for this long.
 * `@llm-ui`'s reveal runs a requestAnimationFrame loop that ONLY stops once
 * `isStreamFinished` is true. A bubble stuck at `isStreaming: true` (dropped
 * NpcStreamEnd, reconnect mid-stream, etc.) would otherwise spin that loop
 * forever, allocating a fresh `BlockMatch` (each carrying a full copy of the
 * output string) every frame → unbounded heap growth → OOM (observed: 2.4GB,
 * millions of identical 2.7KB strings). A real active stream appends chunks far
 * faster than this window, so it never trips; only a wedged bubble does. The
 * window is generous so a legitimately long typewriter animation completes
 * naturally — we only need to cap the pathological infinite case.
 */
const STREAM_STALL_FINISH_MS = 10_000;

export function StreamingMarkdown({ content, isStreaming }: StreamingMarkdownProps) {
  // Watchdog: treat the stream as finished if it claims to be streaming but the
  // content buffer hasn't grown for STREAM_STALL_FINISH_MS. Bounds @llm-ui's
  // rAF loop so a stuck `isStreaming: true` bubble can't leak unboundedly.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!isStreaming) return;
    // New content (or freshly entered streaming) → the stream is live again;
    // clear any prior stall verdict and re-arm the timer for this buffer.
    setStalled(false);
    const timer = setTimeout(() => setStalled(true), STREAM_STALL_FINISH_MS);
    return () => clearTimeout(timer);
    // `content` in deps re-arms on every chunk; the timer only elapses when
    // chunks stop arriving (real end that lost its NpcStreamEnd, or a wedge).
  }, [content, isStreaming]);

  const { blockMatches } = useLLMOutput({
    llmOutput: content,
    blocks: [],
    fallbackBlock: {
      component: MarkdownComponent,
      lookBack: markdownLookBack(),
    },
    isStreamFinished: !isStreaming || stalled,
  });

  // A stalled (force-finished) bubble is no longer live — drop the inline
  // cursor styling + blinking caret so it reads as a settled message.
  const showLive = isStreaming && !stalled;

  return (
    <div
      className={
        showLive
          ? // Force last paragraph to display inline so the blinking cursor
            // sits at the end of the text rather than dropping to a new line.
            '[&>p:last-of-type]:inline'
          : undefined
      }
    >
      {blockMatches.map((blockMatch, index) => {
        const Component = blockMatch.block.component;
        return <Component key={index} blockMatch={blockMatch} />;
      })}
      {showLive && (
        <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse align-middle" />
      )}
    </div>
  );
}
